import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  UserPlus, TrendingUp, TrendingDown, Minus, Upload, Download,
  Search, X, Edit2, Trash2, Users, FileText, ChevronDown, AlertCircle,
  LayoutGrid, List, ChevronLeft, Trophy, Target, Award, Medal, Star,
  DollarSign, Calendar, Clock, BookOpen, MessageSquare, CreditCard,
  ArrowUp, ArrowDown, ChevronRight, ChevronUp, Plus, CheckCircle,
  Send, Save, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import InviteModal from '../../../components/common/InviteModal';
import ProfileAvatar from '../../../profile/components/ProfileAvatar';


// ---------- Constants ----------

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dob: '',
  skillLevel: 'beginner',
  emergencyContactName: '',
  emergencyContactPhone: ''
};

// ---------- Helpers ----------

const getLevelColor = (level) => {
  switch (level) {
    case 'beginner': return 'green';
    case 'intermediate': return 'yellow';
    case 'advanced': return 'red';
    default: return 'gray';
  }
};

const AttendanceTrendIcon = ({ rate }) => {
  if (rate >= 95) return <TrendingUp size={14} className="mr-1 text-green-600" />;
  if (rate >= 80) return <Minus size={14} className="mr-1 text-yellow-500" />;
  return <TrendingDown size={14} className="mr-1 text-red-500" />;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getAttendanceColor = (rate) => {
  if (rate >= 95) return 'text-green-600';
  if (rate >= 80) return 'text-yellow-600';
  return 'text-red-500';
};

// ---------- Export utility ----------

const csvField = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (filename, headers, rows) => {
  const csv = [headers, ...rows].map(r => r.map(csvField).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const exportStudentsCSV = (students) => {
  const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Date of Birth', 'Skill Level', 'Enrolled Classes', 'Attendance Rate', 'Emergency Contact', 'Emergency Phone'];
  const rows = students.map(s => [
    s.firstName,
    s.lastName,
    s.email || '',
    s.phone || '',
    s.dob || '',
    s.skillLevel,
    s.enrolledClasses,
    `${s.attendanceRate}%`,
    s.emergencyContactName || '',
    s.emergencyContactPhone || ''
  ]);
  downloadCSV('students.csv', headers, rows);
};

// ---------- Student Form Modal ----------

const StudentFormModal = ({ isOpen, onClose, onSave, editingStudent }) => {
  const { theme } = useTheme();
  const isEdit = !!editingStudent;
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (isOpen) {
      setFormData(
        editingStudent
          ? {
              firstName: editingStudent.firstName || '',
              lastName: editingStudent.lastName || '',
              email: editingStudent.email || '',
              phone: editingStudent.phone || '',
              dob: editingStudent.dob || '',
              skillLevel: editingStudent.skillLevel || 'beginner',
              emergencyContactName: editingStudent.emergencyContactName || '',
              emergencyContactPhone: editingStudent.emergencyContactPhone || ''
            }
          : EMPTY_FORM
      );
      setErrors({});
    }
  }, [isOpen, editingStudent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  const inputClass = `w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
  const labelClass = `block text-sm font-medium ${theme.text} mb-1`;
  const errorClass = 'text-xs text-red-500 mt-1';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 bg-inherit border-b ${theme.border} p-5 flex items-center justify-between`}>
          <div>
            <h3 className={`text-xl font-semibold ${theme.text}`}>
              {isEdit ? 'Edit Member' : 'Add Member'}
            </h3>
            <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
              {isEdit ? 'Update member information' : 'Enter the details for the new member'}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 ${theme.cardHover} rounded-full transition-colors`}>
            <X className={`w-5 h-5 ${theme.textMuted}`} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Alex" className={inputClass} />
              {errors.firstName && <p className={errorClass}>{errors.firstName}</p>}
            </div>
            <div>
              <label className={labelClass}>Last Name <span className="text-red-500">*</span></label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Martinez" className={inputClass} />
              {errors.lastName && <p className={errorClass}>{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Email <span className="text-red-500">*</span></label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="alex@example.com" className={inputClass} />
              {errors.email && <p className={errorClass}>{errors.email}</p>}
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="555-0100" className={inputClass} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date of Birth</label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Skill Level</label>
              <select name="skillLevel" value={formData.skillLevel} onChange={handleChange} className={inputClass}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>

          <div className={`p-4 rounded-lg border ${theme.border} space-y-3`}>
            <h4 className={`text-sm font-semibold ${theme.text}`}>Emergency Contact</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Contact Name</label>
                <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Contact Phone</label>
                <input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} placeholder="555-0199" className={inputClass} />
              </div>
            </div>
          </div>

          <div className={`flex gap-3 pt-2 border-t ${theme.border}`}>
            <button type="button" onClick={onClose} className={`flex-1 px-4 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text}`}>
              Cancel
            </button>
            <button type="submit" className={`flex-1 px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors`}>
              {isEdit ? 'Save Changes' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------- Bulk Upload Modal (S3 categorized preview, S4 commit + invitations, S5 inline correction) ----------

const SAMPLE_CSV_HEADERS = 'firstName,lastName,email,phone,dob,gender,tShirtSize,skillLevel,emergencyContactName,emergencyContactPhone';
const SAMPLE_CSV_ROWS = [
  'Jordan,Lee,jordan.lee@kynaixsports.com,(555) 100-2001,2011-03-15,Male,M,beginner,David Lee,(555) 100-3001',
  'Taylor,Smith,taylor.smith@kynaixsports.com,(555) 100-2002,2010-07-22,Female,S,intermediate,Susan Smith,(555) 100-3002',
  'Morgan,Davis,morgan.davis@kynaixsports.com,(555) 100-2003,2012-11-08,Male,L,advanced,Karen Davis,(555) 100-3003',
];
const SAMPLE_CSV = [SAMPLE_CSV_HEADERS, ...SAMPLE_CSV_ROWS].join('\n');

const SHEET_MAX_BYTES = 5 * 1024 * 1024;
const SHEET_ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

// Categories a row can be classified into by POST /bulk/analyze, in display order.
const CATEGORY_META = {
  new_user:           { label: 'New Members',        badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  existing_user:      { label: 'Existing Users',       badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  already_member:     { label: 'Already Members',      badge: 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300' },
  duplicate_in_sheet: { label: 'Duplicate in Sheet',   badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  invalid_email:      { label: 'Invalid Email',        badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  missing_required:   { label: 'Missing Required',     badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};
const CATEGORY_ORDER = Object.keys(CATEGORY_META);
// These rows can't be committed as-is — they need an S5 inline correction (then get reclassified).
const BLOCKING_CATEGORIES = new Set(['invalid_email', 'missing_required', 'duplicate_in_sheet']);
// Rows in these categories are pre-checked for commit as soon as they're classified.
const DEFAULT_SELECTED_CATEGORIES = new Set(['new_user', 'existing_user']);

// Strips analyze-only fields (index/category/reason/selected/editing) down to the
// studentInputSchema shape expected by both the analyze (JSON re-validate) and commit endpoints.
const toStudentInput = (row) => ({
  firstName: row.firstName || '',
  lastName: row.lastName || '',
  email: row.email || null,
  phone: row.phone || null,
  dob: row.dob || null,
  gender: row.gender || null,
  tShirtSize: row.tShirtSize || null,
  skillLevel: row.skillLevel || null,
  emergencyContactName: row.emergencyContactName || null,
  emergencyContactPhone: row.emergencyContactPhone || null,
  sexuality: row.sexuality || null,
  grade: row.grade || null,
  waiverSigned: row.waiverSigned == null ? null : !!row.waiverSigned,
  address: row.address || undefined,
  extraInfo: row.extraInfo || undefined,
});

// Merges a bulk/analyze response ({ students, rows: [{index,category,...}] }) into the flat row
// shape the table renders. Preserves each row's prior `selected` choice when its category is
// unchanged (e.g. re-analyzing after fixing a *different* row), so manual toggles aren't lost.
// Also carries forward `inserted`/`insertedVia` by position — a row already committed in an
// earlier partial commit (E3c) must stay locked out of re-submission even if a later edit to a
// *different* row triggers a full-sheet reclassification.
const mergeAnalyzeIntoRows = (analyzeResult, prevRows) => {
  const classByIndex = new Map((analyzeResult.rows || []).map(r => [r.index, r]));
  return (analyzeResult.students || []).map((s, i) => {
    const cls = classByIndex.get(i) || {};
    const prev = prevRows[i];
    const inserted = !!(prev && prev.inserted);
    const blocking = BLOCKING_CATEGORIES.has(cls.category);
    let selected;
    if (inserted || blocking) selected = false;
    else if (prev && prev.category === cls.category) selected = prev.selected;
    else selected = DEFAULT_SELECTED_CATEGORIES.has(cls.category);
    return { ...s, index: i, category: cls.category, reason: cls.reason || null, matchedUserId: cls.matchedUserId || null, selected, editing: false, inserted, insertedVia: prev?.insertedVia || null };
  });
};

const BulkUploadModal = ({ isOpen, onClose, onUploaded, academyId }) => {
  const { theme } = useTheme();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // 'upload' | 'analyzing' | 'preview' | 'revalidating' | 'committing'
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [parseInfo, setParseInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [editDraft, setEditDraft] = useState(null); // { index, firstName, lastName, email, phone }
  const [sendInvitations, setSendInvitations] = useState(false);
  const [hideInserted, setHideInserted] = useState(true); // E4: hide already-committed rows by default

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setIsDragging(false);
      setFileName('');
      setFileError('');
      setParseInfo(null);
      setRows([]);
      setActiveCategory('all');
      setEditDraft(null);
      setSendInvitations(false);
      setHideInserted(true);
    }
  }, [isOpen]);

  const counts = React.useMemo(() => {
    const c = {};
    CATEGORY_ORDER.forEach(k => { c[k] = 0; });
    rows.forEach(r => { if (r.category && c[r.category] !== undefined) c[r.category] += 1; });
    return c;
  }, [rows]);
  const selectedCount = React.useMemo(() => rows.filter(r => r.selected).length, [rows]);
  const blockingCount = React.useMemo(() => rows.filter(r => BLOCKING_CATEGORIES.has(r.category)).length, [rows]);
  const insertedCount = React.useMemo(() => rows.filter(r => r.inserted).length, [rows]);
  const visibleRows = React.useMemo(() => {
    let list = activeCategory === 'all' ? rows : rows.filter(r => r.category === activeCategory);
    if (hideInserted) list = list.filter(r => !r.inserted);
    return list;
  }, [rows, activeCategory, hideInserted]);

  // ── S3: upload → analyze (parses + classifies in one call) ──
  const processFile = async (file) => {
    if (!file) return;
    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
    if (!SHEET_ALLOWED_EXTENSIONS.includes(ext)) { setFileError('Please upload a .csv, .xlsx, or .xls file'); return; }
    if (file.size > SHEET_MAX_BYTES) { setFileError('File too large — max 5MB'); return; }
    setFileName(file.name);
    setFileError('');
    setStep('analyzing');
    try {
      const result = await academyAPI.members.bulkAnalyze(academyId, { file });
      setRows(mergeAnalyzeIntoRows(result, []));
      setParseInfo(result.parseInfo || null);
      setActiveCategory('all');
      setStep('preview');
    } catch (err) {
      toast.error(err.message || 'Failed to analyze sheet');
      setFileError(err.message || 'Failed to analyze sheet');
      setStep('upload');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    processFile(e.dataTransfer.files[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyId]);

  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleFileChange = (e) => { processFile(e.target.files[0]); e.target.value = ''; };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── S3: toggle checkboxes ──
  const toggleRow = (index) => {
    setRows(prev => prev.map(r => r.index === index && !BLOCKING_CATEGORIES.has(r.category) && !r.inserted
      ? { ...r, selected: !r.selected, commitError: undefined }
      : r));
  };
  const toggleCategoryAll = (category, checked) => {
    setRows(prev => prev.map(r => r.category === category && !BLOCKING_CATEGORIES.has(category) && !r.inserted ? { ...r, selected: checked } : r));
  };

  // ── E3a: global selection controls (distinct from the per-category header checkbox above,
  // which only ever acts within one active category tab) ──
  const isSelectableRow = (r) => !BLOCKING_CATEGORIES.has(r.category) && !r.inserted;
  const selectAllVisible = () => {
    const visibleIdx = new Set(visibleRows.filter(isSelectableRow).map(r => r.index));
    setRows(prev => prev.map(r => visibleIdx.has(r.index) ? { ...r, selected: true } : r));
  };
  const selectGoodRows = () => setRows(prev => prev.map(r => isSelectableRow(r) ? { ...r, selected: true } : r));
  const clearAllSelected = () => setRows(prev => prev.map(r => r.selected ? { ...r, selected: false } : r));

  // ── E3b: offline fix + re-upload — export only the rows blocking commit ──
  const handleDownloadBadRows = () => {
    const badRows = rows.filter(r => BLOCKING_CATEGORIES.has(r.category));
    const headers = ['firstName', 'lastName', 'email', 'phone', 'dob', 'gender', 'tShirtSize', 'skillLevel', 'emergencyContactName', 'emergencyContactPhone', 'reason'];
    const data = badRows.map(r => [r.firstName, r.lastName, r.email, r.phone, r.dob, r.gender, r.tShirtSize, r.skillLevel, r.emergencyContactName, r.emergencyContactPhone, r.reason]);
    downloadCSV('bulk_upload_needs_fixing.csv', headers, data);
  };

  // ── S5: inline correction ──
  const startEdit = (row) => setEditDraft({ index: row.index, firstName: row.firstName || '', lastName: row.lastName || '', email: row.email || '', phone: row.phone || '' });
  const cancelEdit = () => setEditDraft(null);
  const saveEdit = async () => {
    if (!editDraft) return;
    const updatedRows = rows.map(r => r.index === editDraft.index
      ? { ...r, firstName: editDraft.firstName, lastName: editDraft.lastName, email: editDraft.email, phone: editDraft.phone }
      : r);
    setEditDraft(null);
    setStep('revalidating');
    try {
      const result = await academyAPI.members.bulkAnalyze(academyId, { students: updatedRows.map(toStudentInput) });
      setRows(mergeAnalyzeIntoRows(result, updatedRows));
      setStep('preview');
    } catch (err) {
      toast.error(err.message || 'Failed to revalidate — your edit was kept locally, try again');
      setRows(updatedRows);
      setStep('preview');
    }
  };

  // ── S4/E3c: selective, partial commit + invitation opt-in — stays in the preview so the
  // uploader can keep working the remaining rows instead of being kicked out to a results screen ──
  const handleCommit = async () => {
    const selectedRows = rows.filter(r => r.selected);
    if (selectedRows.length === 0) { toast.error('Select at least one row to commit'); return; }
    setStep('committing');
    try {
      const result = await academyAPI.members.bulkCommit(academyId, selectedRows.map(toStudentInput), sendInvitations);
      const enrichedResults = result.results.map((r, i) => ({ ...selectedRows[i], ...r }));
      const outcomeByIndex = new Map(enrichedResults.map(r => [r.index, r]));
      setRows(prev => prev.map(r => {
        const outcome = outcomeByIndex.get(r.index);
        if (!outcome) return r;
        return outcome.success
          ? { ...r, inserted: true, insertedVia: outcome.invited ? 'invited' : 'added', selected: false, commitError: undefined }
          : { ...r, commitError: outcome.error };
      }));
      onUploaded({ ...result, results: enrichedResults });
      setStep('preview');
      const directAdds = result.succeeded - result.invited;
      const parts = [];
      if (directAdds > 0) parts.push(`${directAdds} added`);
      if (result.invited > 0) parts.push(`${result.invited} invited`);
      if (result.failed > 0) parts.push(`${result.failed} failed`);
      toast[result.failed > 0 && result.succeeded === 0 ? 'error' : 'success'](parts.join(', ') || 'No rows processed');
    } catch (err) {
      toast.error(err.message || 'Commit failed. Please try again.');
      setStep('preview');
    }
  };

  if (!isOpen) return null;

  const isWide = step === 'preview' || step === 'revalidating';
  const inputClass = `w-full px-2 py-1 text-sm border ${theme.border} rounded ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl ${isWide ? 'max-w-6xl' : 'max-w-2xl'} w-full max-h-[90vh] flex flex-col transition-all duration-200`}>

        {/* Header */}
        <div className={`shrink-0 border-b ${theme.border} px-5 py-4 flex items-center justify-between`}>
          <div>
            <h3 className={`text-xl font-semibold ${theme.text}`}>Bulk Upload Members</h3>
            <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
              {step === 'upload'       && 'Upload a sheet to add multiple students at once'}
              {step === 'analyzing'    && 'Analyzing your sheet…'}
              {step === 'preview'      && `${rows.length} row${rows.length !== 1 ? 's' : ''} categorized — review before committing`}
              {step === 'revalidating' && 'Re-checking your correction…'}
              {step === 'committing'   && `Committing ${selectedCount} student${selectedCount !== 1 ? 's' : ''}…`}
            </p>
          </div>
          {step !== 'committing' && (
            <button onClick={onClose} className={`p-2 ${theme.cardHover} rounded-full transition-colors`}>
              <X className={`w-5 h-5 ${theme.textMuted}`} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* ── Step: Upload ── */}
          {step === 'upload' && (
            <div className="space-y-5">
              <div className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${theme.border} ${theme.background}`}>
                <div className="flex items-start gap-2 min-w-0">
                  <FileText size={18} className="text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className={`text-sm font-medium ${theme.text} mb-1`}>Any column layout works</p>
                    <p className={`text-xs ${theme.textSecondary}`}>
                      AI-assisted parsing maps arbitrary headers (.csv, .xlsx, .xls, max 5MB) to student fields — first/last name, email, phone, DOB, and more.
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadSample} className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 font-medium whitespace-nowrap shrink-0">
                  <Download size={15} /><span>Sample CSV</span>
                </button>
              </div>

              <div
                onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : `${theme.border} ${theme.cardHover}`
                }`}
              >
                <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-blue-500' : theme.textMuted}`} />
                <p className={`font-medium ${theme.text} mb-1`}>{isDragging ? 'Drop your file here' : 'Drag & drop your sheet here'}</p>
                <p className={`text-sm ${theme.textSecondary}`}>or click to browse</p>
                {fileName && <p className="mt-2 text-sm text-blue-600 font-medium">{fileName}</p>}
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" />
              </div>

              {fileError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 shrink-0" />
                  <span className="text-sm text-red-600">{fileError}</span>
                </div>
              )}
            </div>
          )}

          {/* ── Step: Analyzing / Revalidating ── */}
          {(step === 'analyzing' || step === 'revalidating') && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-5" />
              <p className={`text-lg font-semibold ${theme.text} mb-1`}>
                {step === 'analyzing' ? `Analyzing ${fileName}…` : 'Re-checking correction…'}
              </p>
              <p className={`text-sm ${theme.textSecondary}`}>
                {step === 'analyzing' ? 'Parsing columns and checking against existing members.' : 'Reclassifying the sheet against existing members.'}
              </p>
            </div>
          )}

          {/* ── Step: Preview (S3 summary + S5 inline correction) ── */}
          {step === 'preview' && (
            <div className="space-y-4">
              {parseInfo && parseInfo.method === 'ai' && (
                <div className="flex items-center gap-2 p-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                  <CheckCircle size={14} className="shrink-0" />
                  AI-mapped {parseInfo.headers?.length || 0} column{parseInfo.headers?.length !== 1 ? 's' : ''} from your sheet.
                </div>
              )}

              {/* Category summary cards */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`text-center p-2.5 rounded-lg border transition-colors ${activeCategory === 'all' ? 'border-blue-500 ring-1 ring-blue-500' : theme.border} ${theme.cardHover}`}
                >
                  <div className={`text-xl font-bold ${theme.text}`}>{rows.length}</div>
                  <div className={`text-[11px] mt-0.5 ${theme.textSecondary}`}>All Rows</div>
                </button>
                {CATEGORY_ORDER.map(key => (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`text-center p-2.5 rounded-lg border transition-colors ${activeCategory === key ? 'border-blue-500 ring-1 ring-blue-500' : theme.border} ${theme.cardHover}`}
                  >
                    <div className={`text-xl font-bold ${theme.text}`}>{counts[key]}</div>
                    <div className={`text-[11px] mt-0.5 ${theme.textSecondary}`}>{CATEGORY_META[key].label}</div>
                  </button>
                ))}
              </div>

              {blockingCount > 0 && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-300">
                    {blockingCount} row{blockingCount !== 1 ? 's need' : ' needs'} correction before {blockingCount !== 1 ? 'they' : 'it'} can be included — click <strong>Fix</strong> to edit inline.
                  </span>
                </div>
              )}

              {/* E3a/E3b/E4: global selection + offline-fix + partial-commit filter controls */}
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <button type="button" onClick={selectAllVisible} className="font-medium text-blue-500 hover:text-blue-700">Select all</button>
                <button type="button" onClick={selectGoodRows} className="font-medium text-blue-500 hover:text-blue-700">Select good rows</button>
                <button type="button" onClick={clearAllSelected} className="font-medium text-blue-500 hover:text-blue-700">Clear all</button>
                {insertedCount > 0 && (
                  <label className="flex items-center gap-1.5 cursor-pointer select-none ml-1">
                    <input type="checkbox" checked={hideInserted} onChange={e => setHideInserted(e.target.checked)} />
                    <span className={theme.textSecondary}>Hide added rows ({insertedCount})</span>
                  </label>
                )}
                <div className="flex-1" />
                {blockingCount > 0 && (
                  <button type="button" onClick={handleDownloadBadRows} className="flex items-center gap-1 font-medium text-red-500 hover:text-red-700">
                    <Download size={13} /> Download bad rows ({blockingCount})
                  </button>
                )}
              </div>

              {/* Row-level detail table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm min-w-[880px]">
                  <thead className={`${theme.background} border-b ${theme.border}`}>
                    <tr>
                      <th className="px-3 py-2.5 w-8">
                        {activeCategory !== 'all' && !BLOCKING_CATEGORIES.has(activeCategory) && (() => {
                          const selectable = visibleRows.filter(r => !r.inserted);
                          return (
                            <input
                              type="checkbox"
                              checked={selectable.length > 0 && selectable.every(r => r.selected)}
                              onChange={(e) => toggleCategoryAll(activeCategory, e.target.checked)}
                            />
                          );
                        })()}
                      </th>
                      {['#', 'First Name', 'Last Name', 'Email', 'Phone', 'Category', 'Reason', ''].map(col => (
                        <th key={col} className={`px-3 py-2.5 text-left font-semibold text-xs ${theme.textSecondary} whitespace-nowrap uppercase tracking-wide`}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => {
                      const isBlocking = BLOCKING_CATEGORIES.has(row.category);
                      const isEditing = editDraft && editDraft.index === row.index;
                      return (
                        <tr key={row.index} className={`border-b ${theme.border} last:border-0 ${theme.cardHover} ${isEditing ? 'bg-blue-50 dark:bg-blue-900/10' : ''} ${row.inserted ? 'opacity-60' : ''}`}>
                          <td className="px-3 py-2">
                            {row.inserted ? (
                              <CheckCircle size={15} className="text-green-500" />
                            ) : !isBlocking && (
                              <input type="checkbox" checked={!!row.selected} onChange={() => toggleRow(row.index)} />
                            )}
                          </td>
                          <td className={`px-3 py-2 text-xs ${theme.textMuted}`}>{row.index + 1}</td>
                          {isEditing ? (
                            <>
                              <td className="px-3 py-2"><input className={inputClass} value={editDraft.firstName} onChange={e => setEditDraft(d => ({ ...d, firstName: e.target.value }))} placeholder="First name" /></td>
                              <td className="px-3 py-2"><input className={inputClass} value={editDraft.lastName} onChange={e => setEditDraft(d => ({ ...d, lastName: e.target.value }))} placeholder="Last name" /></td>
                              <td className="px-3 py-2"><input className={inputClass} value={editDraft.email} onChange={e => setEditDraft(d => ({ ...d, email: e.target.value }))} placeholder="Email" /></td>
                              <td className="px-3 py-2"><input className={inputClass} value={editDraft.phone} onChange={e => setEditDraft(d => ({ ...d, phone: e.target.value }))} placeholder="Phone" /></td>
                              <td className="px-3 py-2 text-xs" colSpan={2}>editing…</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <button onClick={saveEdit} className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700 mr-2">
                                  <Save size={13} /> Save
                                </button>
                                <button onClick={cancelEdit} className={`text-xs font-medium ${theme.textMuted} hover:${theme.text}`}>Cancel</button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`px-3 py-2 font-medium ${theme.text} whitespace-nowrap`}>{row.firstName || <span className="text-red-400 italic">missing</span>}</td>
                              <td className={`px-3 py-2 ${theme.text} whitespace-nowrap`}>{row.lastName || <span className="text-red-400 italic">missing</span>}</td>
                              <td className={`px-3 py-2 ${theme.textSecondary} max-w-[160px] truncate`}>{row.email || <span className="text-red-400 italic">missing</span>}</td>
                              <td className={`px-3 py-2 ${theme.textSecondary} whitespace-nowrap`}>{row.phone}</td>
                              <td className="px-3 py-2">
                                {row.inserted ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                    {row.insertedVia === 'invited' ? 'Invited' : 'Added'}
                                  </span>
                                ) : (
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${CATEGORY_META[row.category]?.badge || 'bg-gray-100 text-gray-600'}`}>
                                    {CATEGORY_META[row.category]?.label || row.category || '—'}
                                  </span>
                                )}
                              </td>
                              <td className={`px-3 py-2 text-xs max-w-[220px] truncate ${row.commitError ? 'text-red-500' : theme.textSecondary}`}>{row.commitError || row.reason || '—'}</td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {!row.inserted && (
                                  <button onClick={() => startEdit(row)} className="inline-flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-700">
                                    <Edit2 size={13} /> {isBlocking ? 'Fix' : 'Edit'}
                                  </button>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                    {visibleRows.length === 0 && (
                      <tr><td colSpan={8} className={`px-3 py-6 text-center text-sm ${theme.textMuted}`}>No rows in this category</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Step: Committing ── */}
          {step === 'committing' && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-5" />
              <p className={`text-lg font-semibold ${theme.text} mb-1`}>Committing {selectedCount} members…</p>
              <p className={`text-sm ${theme.textSecondary}`}>
                {sendInvitations ? 'Sending academy invitations. ' : 'Adding to academy. '}Please don't close this window.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`shrink-0 border-t ${theme.border} px-5 py-4`}>
          {step === 'preview' && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none shrink-0">
                <input type="checkbox" checked={sendInvitations} onChange={e => setSendInvitations(e.target.checked)} />
                <span className={theme.text}>Send invitations</span>
                <span className={`text-xs ${theme.textMuted}`}>(unchecked = add directly)</span>
              </label>
              <div className="flex-1" />
              <button type="button" onClick={onClose} className={`px-4 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text}`}>
                Close
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={selectedCount === 0}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {sendInvitations ? <Send size={15} /> : <UserPlus size={15} />}
                {sendInvitations ? 'Invite' : 'Add'} {selectedCount} Selected
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Remove Confirmation ----------

const RemoveConfirm = ({ student, onConfirm, onCancel }) => {
  const { theme } = useTheme();
  if (!student) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl max-w-sm w-full p-6`}>
        <div className="flex items-center gap-x-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Trash2 size={20} className="text-red-600" />
          </div>
          <h3 className={`text-lg font-semibold ${theme.text}`}>Remove Member</h3>
        </div>
        <p className={`${theme.textSecondary} mb-5`}>
          Are you sure you want to remove <strong className={theme.text}>{student.firstName} {student.lastName}</strong> from the academy? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className={`flex-1 px-4 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text}`}>
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkRemoveConfirm = ({ isOpen, count, onConfirm, onCancel }) => {
  const { theme } = useTheme();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl max-w-sm w-full p-6`}>
        <div className="flex items-center gap-x-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Trash2 size={20} className="text-red-600" />
          </div>
          <h3 className={`text-lg font-semibold ${theme.text}`}>Remove Members</h3>
        </div>
        <p className={`${theme.textSecondary} mb-5`}>
          Are you sure you want to remove <strong className={theme.text}>{count} member{count !== 1 ? 's' : ''}</strong> from the academy? This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onCancel} className={`flex-1 px-4 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text}`}>
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Student Profile View ----------

const StudentProfileView = ({ student, onBack, isAdmin, onUpdateStudent }) => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSport, setExpandedSport] = useState(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [notes, setNotes] = useState(student.notes || []);
  const [paymentHistory, setPaymentHistory] = useState(student.paymentHistory || []);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', description: '', date: '' });

  const totalSessionsAttended = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.sessionsAttended, 0);
  const totalSessions = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.totalSessions, 0);
  const overallAttendance = totalSessions > 0 ? Math.round((totalSessionsAttended / totalSessions) * 100) : 0;

  const totalFees = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.fee, 0);
  const totalPaid = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.paid, 0);
  const totalOutstanding = totalFees - totalPaid;

  const profileTabs = [
    { id: 'overview', label: 'Overview', icon: <Users size={15} /> },
    { id: 'stats', label: 'Stats', icon: <Trophy size={15} /> },
    { id: 'attendance', label: 'Attendance', icon: <Clock size={15} /> },
    { id: 'notes', label: 'Notes', icon: <MessageSquare size={15} /> },
    { id: 'payments', label: 'Payments', icon: <CreditCard size={15} /> }
  ];

  const handleAddNote = () => {
    if (!newNoteText.trim()) return;
    const newNote = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      author: 'Admin',
      content: newNoteText.trim()
    };
    const updatedNotes = [newNote, ...notes];
    setNotes(updatedNotes);
    setNewNoteText('');
    setShowNoteInput(false);
    toast.success('Note added');
  };

  const handleRecordPayment = () => {
    if (!paymentForm.amount || !paymentForm.description || !paymentForm.date) {
      toast.error('Please fill in all payment fields');
      return;
    }
    const newPayment = {
      id: Date.now(),
      date: paymentForm.date,
      amount: parseFloat(paymentForm.amount),
      description: paymentForm.description,
      status: 'paid'
    };
    setPaymentHistory(prev => [newPayment, ...prev]);
    setPaymentForm({ amount: '', description: '', date: '' });
    setShowPaymentForm(false);
    toast.success('Payment recorded');
  };

  const statCard = (label, value, icon, color = 'blue') => (
    <div className={`${theme.card} border ${theme.border} rounded-xl p-4 flex items-center gap-x-3`}>
      <div className={`p-2 bg-${color}-100 dark:bg-${color}-900/30 rounded-lg`}>
        <span className={`text-${color}-600`}>{icon}</span>
      </div>
      <div>
        <p className={`text-xs ${theme.textSecondary}`}>{label}</p>
        <p className={`text-lg font-bold ${theme.text}`}>{value}</p>
      </div>
    </div>
  );

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className={`flex items-center gap-x-2 mb-5 text-sm ${theme.textSecondary} hover:${theme.text} transition-colors`}
      >
        <ChevronLeft size={16} />
        <span>Back to Members</span>
      </button>

      {/* Profile Header */}
      <div className={`${theme.card} border ${theme.border} rounded-xl p-6 mb-5`}>
        <div className="flex flex-col sm:flex-row sm:items-start gap-5">
          <ProfileAvatar
            userId={student.userId}
            profileImage={student.profileImage}
            firstName={student.firstName}
            lastName={student.lastName}
            size="2xl"
            className="ring-4 ring-offset-2 ring-blue-100 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className={`text-2xl font-bold ${theme.text}`}>{student.firstName} {student.lastName}</h2>
              <span className={`px-2.5 py-0.5 bg-${getLevelColor(student.skillLevel)}-100 text-${getLevelColor(student.skillLevel)}-700 rounded text-xs font-semibold capitalize`}>
                {student.skillLevel}
              </span>
              <span className={`px-2.5 py-0.5 rounded text-xs font-semibold ${student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {student.status === 'active' ? 'Active' : 'Invited'}
              </span>
            </div>
            <div className={`flex flex-wrap gap-x-4 gap-y-1 text-sm ${theme.textSecondary} mb-4`}>
              {student.email && <span>{student.email}</span>}
              {student.phone && <span>{student.phone}</span>}
              {student.dob && <span>DOB: {formatDate(student.dob)}</span>}
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className={`${theme.background} border ${theme.border} rounded-lg p-3 text-center`}>
                <p className={`text-lg font-bold ${theme.text}`}>{totalSessionsAttended}/{totalSessions}</p>
                <p className={`text-xs ${theme.textSecondary}`}>Sessions</p>
              </div>
              <div className={`${theme.background} border ${theme.border} rounded-lg p-3 text-center`}>
                <p className={`text-lg font-bold ${getAttendanceColor(overallAttendance)}`}>{overallAttendance}%</p>
                <p className={`text-xs ${theme.textSecondary}`}>Attendance</p>
              </div>
              <div className={`${theme.background} border ${theme.border} rounded-lg p-3 text-center`}>
                <p className={`text-lg font-bold ${theme.text}`}>{(student.sportRankings || []).length}</p>
                <p className={`text-xs ${theme.textSecondary}`}>Sport Rankings</p>
              </div>
              <div className={`${theme.background} border ${theme.border} rounded-lg p-3 text-center`}>
                <p className={`text-lg font-bold ${student.dues && student.dues.amount > 0 ? 'text-red-500' : 'text-green-600'}`}>
                  {student.dues && student.dues.amount > 0 ? `$${student.dues.amount}` : '$0'}
                </p>
                <p className={`text-xs ${theme.textSecondary}`}>Dues</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Tabs */}
      <div className={`border-b ${theme.border} mb-5`}>
        <div className="flex gap-1 overflow-x-auto">
          {profileTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-x-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:${theme.text}`
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Enrolled Classes */}
          <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
            <h3 className={`text-base font-semibold ${theme.text} mb-4`}>Enrolled Classes</h3>
            {(student.enrolledClassesList || []).length === 0 ? (
              <p className={`text-sm ${theme.textSecondary}`}>No classes enrolled yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${theme.border}`}>
                      {['Class Name', 'Sport', 'Coach', 'Sessions', 'Attendance', 'Fee', 'Paid', 'Due', 'Status'].map(col => (
                        <th key={col} className={`text-left py-2 px-3 font-medium ${theme.textSecondary}`}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {student.enrolledClassesList.map(cls => {
                      const attRate = cls.totalSessions > 0 ? Math.round((cls.sessionsAttended / cls.totalSessions) * 100) : 0;
                      const isPaid = cls.paid >= cls.fee;
                      return (
                        <tr key={cls.classId} className={`border-b ${theme.border} last:border-0`}>
                          <td className={`py-2.5 px-3 font-medium ${theme.text}`}>{cls.className}</td>
                          <td className={`py-2.5 px-3 ${theme.textSecondary}`}>{cls.sport}</td>
                          <td className={`py-2.5 px-3 ${theme.textSecondary}`}>{cls.coach}</td>
                          <td className={`py-2.5 px-3 ${theme.text}`}>{cls.sessionsAttended}/{cls.totalSessions}</td>
                          <td className={`py-2.5 px-3 font-medium ${getAttendanceColor(attRate)}`}>{attRate}%</td>
                          <td className={`py-2.5 px-3 ${theme.text}`}>${cls.fee}</td>
                          <td className={`py-2.5 px-3 text-green-600`}>${cls.paid}</td>
                          <td className={`py-2.5 px-3 ${cls.fee - cls.paid > 0 ? 'text-red-500' : theme.textSecondary}`}>
                            ${cls.fee - cls.paid}
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {isPaid ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Emergency Contact */}
          <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
            <h3 className={`text-base font-semibold ${theme.text} mb-3`}>Emergency Contact</h3>
            <div className={`flex flex-wrap gap-x-8 gap-y-2 text-sm`}>
              <div>
                <p className={`${theme.textSecondary} text-xs mb-0.5`}>Name</p>
                <p className={theme.text}>{student.emergencyContactName || '—'}</p>
              </div>
              <div>
                <p className={`${theme.textSecondary} text-xs mb-0.5`}>Phone</p>
                <p className={theme.text}>{student.emergencyContactPhone || '—'}</p>
              </div>
            </div>
          </div>

          {/* Additional Info — unmapped bulk-upload sheet columns (KYN-471/KYN-469 E1) */}
          {student.extraInfo && (student.extraInfo.contactName || student.extraInfo.contactEmail || (student.extraInfo.custom && Object.keys(student.extraInfo.custom).length > 0)) && (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
              <h3 className={`text-base font-semibold ${theme.text} mb-3 flex items-center gap-1.5`}>
                <Info size={15} />
                <span>Additional Info</span>
              </h3>
              <div className={`flex flex-wrap gap-x-8 gap-y-2 text-sm`}>
                {student.extraInfo.contactName && (
                  <div>
                    <p className={`${theme.textSecondary} text-xs mb-0.5`}>Contact Name</p>
                    <p className={theme.text}>{student.extraInfo.contactName}</p>
                  </div>
                )}
                {student.extraInfo.contactEmail && (
                  <div>
                    <p className={`${theme.textSecondary} text-xs mb-0.5`}>Contact Email</p>
                    <p className={theme.text}>{student.extraInfo.contactEmail}</p>
                  </div>
                )}
                {student.extraInfo.custom && Object.entries(student.extraInfo.custom).map(([key, value]) => (
                  <div key={key}>
                    <p className={`${theme.textSecondary} text-xs mb-0.5`}>{key}</p>
                    <p className={theme.text}>{String(value)}</p>
                  </div>
                ))}
              </div>
              <p className={`text-xs ${theme.textSecondary} mt-3`}>From bulk-upload sheet columns that don't map to a standard field.</p>
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-5">
          {/* Overall stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {statCard('Games Played', student.performanceStats.gamesPlayed, <Target size={18} />, 'blue')}
            {statCard('Wins', student.performanceStats.wins, <Trophy size={18} />, 'green')}
            {statCard('Losses', student.performanceStats.losses, <TrendingDown size={18} />, 'red')}
            {statCard('Win Rate', `${student.performanceStats.winRate}%`, <Award size={18} />, 'yellow')}
          </div>

          {/* Recent Form */}
          {(student.performanceStats.recentForm || []).length > 0 && (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
              <h3 className={`text-base font-semibold ${theme.text} mb-3`}>Recent Form (Last 7)</h3>
              <div className="flex flex-wrap gap-2">
                {student.performanceStats.recentForm.map((result, i) => (
                  <span
                    key={i}
                    className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-bold ${
                      result === 'W' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {result}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sport Rankings */}
          {(student.sportRankings || []).length > 0 && (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
              <h3 className={`text-base font-semibold ${theme.text} mb-4`}>Sport Rankings in Academy</h3>
              <div className="space-y-4">
                {student.sportRankings.map((ranking, i) => {
                  const percentile = Math.round((ranking.rank / ranking.outOf) * 100);
                  return (
                    <div key={i} className={`border ${theme.border} rounded-lg p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-x-3">
                          <Medal size={18} className="text-yellow-500" />
                          <span className={`font-semibold ${theme.text}`}>{ranking.sport}</span>
                        </div>
                        <div className="flex items-center gap-x-3">
                          <span className={`text-sm font-bold text-blue-600`}>#{ranking.rank} of {ranking.outOf}</span>
                          <span className={`text-sm ${theme.textSecondary}`}>Rating: {ranking.rating}</span>
                          {ranking.trend === 'up' && <ArrowUp size={14} className="text-green-500" />}
                          {ranking.trend === 'down' && <ArrowDown size={14} className="text-red-500" />}
                          {ranking.trend === 'stable' && <Minus size={14} className="text-yellow-500" />}
                        </div>
                      </div>
                      <div className={`w-full h-2 ${theme.background} rounded-full overflow-hidden`}>
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${Math.max(5, 100 - percentile)}%` }}
                        />
                      </div>
                      <p className={`text-xs ${theme.textSecondary} mt-1`}>Top {percentile}% in academy</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sport Breakdown */}
          {Object.keys(student.performanceStats.sportStats || {}).length > 0 && (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
              <h3 className={`text-base font-semibold ${theme.text} mb-4`}>Sport Breakdown</h3>
              <div className="space-y-2">
                {Object.entries(student.performanceStats.sportStats).map(([sport, stats]) => (
                  <div key={sport} className={`border ${theme.border} rounded-lg overflow-hidden`}>
                    <button
                      onClick={() => setExpandedSport(expandedSport === sport ? null : sport)}
                      className={`w-full flex items-center justify-between px-4 py-3 ${theme.cardHover} transition-colors`}
                    >
                      <div className="flex items-center gap-x-2">
                        <Star size={15} className="text-yellow-500" />
                        <span className={`font-medium ${theme.text}`}>{sport}</span>
                        <span className={`text-xs ${theme.textSecondary}`}>{stats.gamesPlayed} games</span>
                      </div>
                      {expandedSport === sport ? <ChevronUp size={16} className={theme.textMuted} /> : <ChevronDown size={16} className={theme.textMuted} />}
                    </button>
                    {expandedSport === sport && (
                      <div className={`px-4 pb-4 border-t ${theme.border}`}>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                          {[
                            { label: 'Games', value: stats.gamesPlayed },
                            { label: 'Wins', value: stats.wins },
                            { label: 'Losses', value: stats.losses },
                            { label: 'Win Rate', value: `${stats.winRate}%` },
                            { label: 'Avg Score', value: stats.avgScore },
                            { label: 'Avg Pts/Game', value: stats.avgPointsPerGame },
                            { label: 'Longest Streak', value: `${stats.longestWinStreak}W` },
                            { label: 'Titles', value: stats.titles }
                          ].map(item => (
                            <div key={item.label} className={`${theme.background} rounded-lg p-3 text-center border ${theme.border}`}>
                              <p className={`text-sm font-bold ${theme.text}`}>{item.value}</p>
                              <p className={`text-xs ${theme.textSecondary}`}>{item.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="space-y-5">
          {/* Overall summary */}
          <div className={`${theme.card} border ${theme.border} rounded-xl p-5`}>
            <h3 className={`text-base font-semibold ${theme.text} mb-3`}>Overall Attendance</h3>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className={`w-full h-3 ${theme.background} rounded-full overflow-hidden`}>
                  <div
                    className={`h-full rounded-full ${overallAttendance >= 95 ? 'bg-green-500' : overallAttendance >= 80 ? 'bg-yellow-400' : 'bg-red-500'}`}
                    style={{ width: `${overallAttendance}%` }}
                  />
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-xl font-bold ${getAttendanceColor(overallAttendance)}`}>{overallAttendance}%</p>
                <p className={`text-xs ${theme.textSecondary}`}>{totalSessionsAttended}/{totalSessions} sessions</p>
              </div>
            </div>
          </div>

          {/* Per-class breakdown */}
          {(student.enrolledClassesList || []).length === 0 ? (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-8 text-center`}>
              <Clock size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
              <p className={theme.textSecondary}>No classes enrolled yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {student.enrolledClassesList.map(cls => {
                const attRate = cls.totalSessions > 0 ? Math.round((cls.sessionsAttended / cls.totalSessions) * 100) : 0;
                return (
                  <div key={cls.classId} className={`${theme.card} border ${theme.border} rounded-xl p-4`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className={`font-semibold ${theme.text}`}>{cls.className}</p>
                        <p className={`text-xs ${theme.textSecondary}`}>Coach: {cls.coach}</p>
                      </div>
                      <span className={`text-lg font-bold ${getAttendanceColor(attRate)}`}>{attRate}%</span>
                    </div>
                    <div className={`w-full h-2 ${theme.background} rounded-full overflow-hidden mb-1`}>
                      <div
                        className={`h-full rounded-full ${attRate >= 95 ? 'bg-green-500' : attRate >= 80 ? 'bg-yellow-400' : 'bg-red-500'}`}
                        style={{ width: `${attRate}%` }}
                      />
                    </div>
                    <p className={`text-xs ${theme.textSecondary}`}>{cls.sessionsAttended} of {cls.totalSessions} sessions attended</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab === 'notes' && (
        <div className="space-y-4">
          {!isAdmin ? (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-8 text-center`}>
              <MessageSquare size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
              <p className={`font-medium ${theme.text} mb-1`}>No Access</p>
              <p className={theme.textSecondary}>Only administrators can view and add notes.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h3 className={`font-semibold ${theme.text}`}>{notes.length} Note{notes.length !== 1 ? 's' : ''}</h3>
                <button
                  onClick={() => setShowNoteInput(v => !v)}
                  className={`flex items-center gap-x-1.5 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm`}
                >
                  <Plus size={14} />
                  <span>Add Note</span>
                </button>
              </div>

              {showNoteInput && (
                <div className={`${theme.card} border ${theme.border} rounded-xl p-4 space-y-3`}>
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Write a note about this member..."
                    rows={3}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none`}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowNoteInput(false); setNewNoteText(''); }}
                      className={`px-3 py-1.5 border ${theme.border} rounded-lg text-sm ${theme.cardHover} ${theme.text}`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddNote}
                      disabled={!newNoteText.trim()}
                      className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm disabled:opacity-40`}
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              )}

              {notes.length === 0 ? (
                <div className={`${theme.card} border ${theme.border} rounded-xl p-8 text-center`}>
                  <MessageSquare size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
                  <p className={theme.textSecondary}>No notes yet. Add the first note above.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map(note => (
                    <div key={note.id} className={`${theme.card} border ${theme.border} rounded-xl p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-semibold ${theme.text}`}>{note.author}</span>
                        <span className={`text-xs ${theme.textSecondary}`}>{formatDate(note.date)}</span>
                      </div>
                      <p className={`text-sm ${theme.textSecondary}`}>{note.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            {statCard('Total Fees', `$${totalFees}`, <DollarSign size={18} />, 'blue')}
            {statCard('Amount Paid', `$${totalPaid}`, <CreditCard size={18} />, 'green')}
            {statCard('Outstanding', `$${totalOutstanding}`, <AlertCircle size={18} />, totalOutstanding > 0 ? 'red' : 'green')}
          </div>

          {/* Record Payment button (admin only) */}
          {isAdmin && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowPaymentForm(v => !v)}
                className={`flex items-center gap-x-1.5 px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm`}
              >
                <Plus size={14} />
                <span>Record Payment</span>
              </button>
            </div>
          )}

          {/* Payment form */}
          {showPaymentForm && isAdmin && (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-4 space-y-3`}>
              <h4 className={`text-sm font-semibold ${theme.text}`}>Record New Payment</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Amount ($)</label>
                  <input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                    placeholder="150"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 text-sm`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Description</label>
                  <input
                    type="text"
                    value={paymentForm.description}
                    onChange={(e) => setPaymentForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Class payment..."
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 text-sm`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium ${theme.textSecondary} mb-1`}>Date</label>
                  <input
                    type="date"
                    value={paymentForm.date}
                    onChange={(e) => setPaymentForm(p => ({ ...p, date: e.target.value }))}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 text-sm`}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPaymentForm(false)}
                  className={`px-3 py-1.5 border ${theme.border} rounded-lg text-sm ${theme.cardHover} ${theme.text}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-sm`}
                >
                  Save Payment
                </button>
              </div>
            </div>
          )}

          {/* Payment history table */}
          {paymentHistory.length === 0 ? (
            <div className={`${theme.card} border ${theme.border} rounded-xl p-8 text-center`}>
              <CreditCard size={40} className={`mx-auto mb-3 ${theme.textMuted}`} />
              <p className={theme.textSecondary}>No payment history yet.</p>
            </div>
          ) : (
            <div className={`${theme.card} border ${theme.border} rounded-xl overflow-hidden`}>
              <table className="w-full text-sm">
                <thead className={`border-b ${theme.border} ${theme.background}`}>
                  <tr>
                    {['Date', 'Description', 'Amount', 'Status'].map(col => (
                      <th key={col} className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment, i) => (
                    <tr key={payment.id} className={`border-b ${theme.border} last:border-0`}>
                      <td className={`px-4 py-3 ${theme.textSecondary}`}>{formatDate(payment.date)}</td>
                      <td className={`px-4 py-3 ${theme.text}`}>{payment.description}</td>
                      <td className={`px-4 py-3 font-semibold ${theme.text}`}>${payment.amount}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {payment.status === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Main StudentsTab ----------

// Maps a backend `memberSchema` row (from list/add) to the local student-grid shape.
const mapMember = (m) => ({
  userId:               m.userId,
  memberId:             m.memberId,
  firstName:            m.firstName,
  lastName:             m.lastName,
  email:                m.email || '',
  phone:                m.phone || '',
  profileImage:         m.profileImage || null,
  dob:                  m.dob || '',
  gender:               m.gender || '',
  tShirtSize:           m.tShirtSize || '',
  skillLevel:           'beginner',
  enrolledClasses:      0,
  attendanceRate:       0,
  status:               'active',
  isSuspended:          !!m.isSuspended,
  dues:                 null,
  enrolledClassesList:  [],
  sportRankings:        [],
  performanceStats:     { gamesPlayed: 0, wins: 0, losses: 0, winRate: 0, recentForm: [], sportStats: {} },
  notes:                [],
  paymentHistory:       [],
  // Unmapped bulk-upload sheet columns (KYN-471 contract): { version, contactName, contactEmail, custom }
  extraInfo:            m.extraInfo || null,
});

const StudentsTab = ({ academyId, userRole, academyName = 'Elite Tennis Academy', onAcademyUpdate }) => {
  const { theme } = useTheme();

  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [skillFilter, setSkillFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [duesPendingOnly, setDuesPendingOnly] = useState(false);
  const [classFilter, setClassFilter] = useState('all');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [removingStudent, setRemovingStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);

  const isAdmin = userRole === 'admin' || userRole === 'owner';

  useEffect(() => {
    if (!academyId) return;
    setIsLoading(true);
    academyAPI.members.list(academyId, { limit: 200 })
      .then(data => setStudents((data.members || []).map(mapMember)))
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setIsLoading(false));
  }, [academyId]);

  // Derive unique class options from all students
  const allClasses = Array.from(
    new Set(
      students.flatMap(s => (s.enrolledClassesList || []).map(c => c.className))
    )
  );

  // Filtered list
  const filteredStudents = students.filter(s => {
    const fullName = `${s.firstName} ${s.lastName}`.toLowerCase();
    const matchesSearch = !searchQuery || fullName.includes(searchQuery.toLowerCase());
    const matchesSkill = skillFilter === 'all' || s.skillLevel === skillFilter;
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchesDues = !duesPendingOnly || (s.dues && s.dues.amount > 0);
    const matchesClass = classFilter === 'all' || (s.enrolledClassesList || []).some(c => c.className === classFilter);
    return matchesSearch && matchesSkill && matchesStatus && matchesDues && matchesClass;
  });

  const hasActiveFilters = searchQuery || skillFilter !== 'all' || statusFilter !== 'all' || duesPendingOnly || classFilter !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setSkillFilter('all');
    setStatusFilter('all');
    setDuesPendingOnly(false);
    setClassFilter('all');
  };

  // --- Add / Edit ---
  const handleOpenAdd = () => setShowInviteModal(true);

  const handleOpenEdit = (student) => {
    setEditingStudent(student);
    setShowFormModal(true);
  };

  // --- Add / Invite (S1) — InviteModal callbacks, same shared component StaffSettings uses ---
  const handleAddExistingMember = async (user) => {
    const member = await academyAPI.members.add(academyId, { targetUserId: user.id });
    setStudents(prev => [...prev, mapMember(member)]);
    toast.success(`${member.firstName} ${member.lastName} added to the academy`);
    onAcademyUpdate?.();
  };

  // Reuses the academy-invitation primitive (sendAcademyInvitation, KYN-319 S4) — membership
  // isn't created until the invite is accepted, so the invited user isn't added to the grid here.
  const handleInviteByEmail = async ({ firstName, lastName, email }) => {
    await academyAPI.members.invite(academyId, { firstName, lastName, email });
    toast.success(`Invitation sent to ${firstName} ${lastName}`);
  };

  const handleInviteByPhone = async () => {
    throw new Error('Phone invites are not supported for students - use email instead');
  };

  const checkIfAlreadyExistsMember = (userId) =>
    students.some(s => String(s.userId) === userId);

  const handleSaveStudent = (formData) => {
    if (editingStudent) {
      setStudents(prev =>
        prev.map(s =>
          s.userId === editingStudent.userId ? { ...s, ...formData } : s
        )
      );
      toast.success(`${formData.firstName} ${formData.lastName} updated successfully`);
    } else {
      const newStudent = {
        userId: Date.now(),
        profileImage: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70) + 1}`,
        enrolledClasses: 0,
        attendanceRate: 0,
        status: 'active',
        dues: null,
        enrolledClassesList: [],
        sportRankings: [],
        performanceStats: {
          gamesPlayed: 0, wins: 0, losses: 0, winRate: 0,
          recentForm: [], sportStats: {}
        },
        notes: [],
        paymentHistory: [],
        ...formData
      };
      setStudents(prev => [...prev, newStudent]);
      toast.success(`${formData.firstName} ${formData.lastName} added to the academy`);
    }
    setShowFormModal(false);
    setEditingStudent(null);
  };

  // --- Remove ---
  const handleRemoveConfirm = () => {
    if (!removingStudent) return;
    setStudents(prev => prev.filter(s => s.userId !== removingStudent.userId));
    if (selectedStudent && selectedStudent.userId === removingStudent.userId) {
      setSelectedStudent(null);
    }
    toast.success(`${removingStudent.firstName} ${removingStudent.lastName} removed`);
    setRemovingStudent(null);
  };

  // --- Bulk selection ---
  const toggleSelectMember = (memberId) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const allVisibleSelected = filteredStudents.length > 0 &&
    filteredStudents.every(s => selectedMemberIds.has(s.memberId));

  const toggleSelectAllVisible = () => {
    setSelectedMemberIds(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        filteredStudents.forEach(s => next.delete(s.memberId));
        return next;
      }
      const next = new Set(prev);
      filteredStudents.forEach(s => next.add(s.memberId));
      return next;
    });
  };

  const clearSelection = () => setSelectedMemberIds(new Set());

  // --- Bulk actions (S4 remove, S5 suspend) ---
  const handleBulkRemove = async () => {
    const memberIds = [...selectedMemberIds];
    if (memberIds.length === 0) return;
    setBulkActionBusy(true);
    try {
      await academyAPI.members.bulkRemove(academyId, memberIds);
      setStudents(prev => prev.filter(s => !selectedMemberIds.has(s.memberId)));
      toast.success(`${memberIds.length} member${memberIds.length !== 1 ? 's' : ''} removed`);
      clearSelection();
      onAcademyUpdate?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to remove members');
    } finally {
      setBulkActionBusy(false);
      setBulkRemoveConfirmOpen(false);
    }
  };

  const handleBulkSuspend = async (suspended) => {
    const memberIds = [...selectedMemberIds];
    if (memberIds.length === 0) return;
    setBulkActionBusy(true);
    try {
      await academyAPI.members.bulkSuspend(academyId, memberIds, suspended);
      setStudents(prev => prev.map(s =>
        selectedMemberIds.has(s.memberId) ? { ...s, isSuspended: suspended } : s
      ));
      toast.success(`${memberIds.length} member${memberIds.length !== 1 ? 's' : ''} ${suspended ? 'suspended' : 'reactivated'}`);
      clearSelection();
    } catch (err) {
      toast.error(err?.message || 'Failed to update suspension status');
    } finally {
      setBulkActionBusy(false);
    }
  };

  // --- Bulk resend invite (S3) ---
  const handleBulkResendInvite = async () => {
    const memberIds = [...selectedMemberIds];
    if (memberIds.length === 0) return;
    setBulkActionBusy(true);
    try {
      const result = await academyAPI.members.bulkResendInvite(academyId, memberIds);
      toast.success(`Invitation resent to ${result.sent} member${result.sent !== 1 ? 's' : ''}`);
      clearSelection();
    } catch (err) {
      toast.error(err?.message || 'Failed to resend invitations');
    } finally {
      setBulkActionBusy(false);
    }
  };

  // --- Bulk Upload ---
  // Called by BulkUploadModal after each /bulk/commit — may fire multiple times per modal session
  // now that partial commits keep the modal open (E3c), so this must not close the modal itself;
  // the modal already toasts the succeeded/invited/failed breakdown, this just reflects direct-adds
  // into the local grid. Invited rows aren't academy members yet (membership is created on
  // invite-accept), so they're intentionally left out of the grid until then.
  const handleBulkUploaded = (enrichedResult) => {
    const directAdds = enrichedResult.results.filter(r => r.success && !r.invited);
    if (directAdds.length > 0) {
      const newStudents = directAdds.map((r, i) => ({
        userId: r.userId || Date.now() + i,
        firstName: r.firstName || '',
        lastName: r.lastName || '',
        email: r.email || '',
        phone: r.phone || '',
        skillLevel: (r.skillLevel || 'beginner').toLowerCase(),
        profileImage: r.profileImage || null,
        enrolledClasses: 0,
        attendanceRate: 0,
        dob: r.dob || '',
        gender: r.gender || '',
        tShirtSize: r.tShirtSize || '',
        emergencyContactName: r.emergencyContactName || '',
        emergencyContactPhone: r.emergencyContactPhone || '',
        status: 'active',
        dues: null,
        enrolledClassesList: [],
        sportRankings: [],
        performanceStats: { gamesPlayed: 0, wins: 0, losses: 0, winRate: 0, recentForm: [], sportStats: {} },
        notes: [],
        paymentHistory: []
      }));
      setStudents(prev => [...prev, ...newStudents]);
      onAcademyUpdate?.();
    }
  };

  // --- Export ---
  const handleExport = () => {
    exportStudentsCSV(filteredStudents);
    toast.success(`Exported ${filteredStudents.length} member${filteredStudents.length !== 1 ? 's' : ''} to CSV`);
  };

  // If a student profile is selected, show their full profile view
  if (selectedStudent) {
    return (
      <div>
        <StudentProfileView
          student={selectedStudent}
          onBack={() => setSelectedStudent(null)}
          isAdmin={isAdmin}
          onUpdateStudent={(updated) => {
            setStudents(prev => prev.map(s => s.userId === updated.userId ? updated : s));
            setSelectedStudent(updated);
          }}
        />
        <RemoveConfirm
          student={removingStudent}
          onConfirm={handleRemoveConfirm}
          onCancel={() => setRemovingStudent(null)}
        />
        <StudentFormModal
          isOpen={showFormModal}
          onClose={() => { setShowFormModal(false); setEditingStudent(null); }}
          onSave={handleSaveStudent}
          editingStudent={editingStudent}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className={`text-2xl font-bold ${theme.text}`}>Members</h3>
          <p className={theme.textSecondary}>{students.length} member{students.length !== 1 ? 's' : ''} enrolled</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Select all visible (works across grid/table view) */}
          {isAdmin && filteredStudents.length > 0 && (
            <label className={`flex items-center gap-1.5 px-3 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors text-sm ${theme.text} cursor-pointer`}>
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                className="w-4 h-4 rounded cursor-pointer"
              />
              <span>Select all</span>
            </label>
          )}

          {/* View Toggle */}
          <div className={`flex border ${theme.border} rounded-lg overflow-hidden`}>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-x-1.5 px-3 py-2 text-sm transition-colors ${
                viewMode === 'grid'
                  ? `${theme.buttonPrimary}`
                  : `${theme.cardHover} ${theme.textSecondary}`
              }`}
              title="Grid view"
            >
              <LayoutGrid size={15} />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-x-1.5 px-3 py-2 text-sm transition-colors border-l ${theme.border} ${
                viewMode === 'table'
                  ? `${theme.buttonPrimary}`
                  : `${theme.cardHover} ${theme.textSecondary}`
              }`}
              title="Table view"
            >
              <List size={15} />
              <span>Table</span>
            </button>
          </div>

          {/* Export */}
          <button
            onClick={handleExport}
            className={`flex items-center gap-x-2 px-3 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text} text-sm`}
          >
            <Download size={16} />
            <span>Export</span>
          </button>

          {isAdmin && (
            <>
              <button
                onClick={() => setShowBulkModal(true)}
                className={`flex items-center gap-x-2 px-3 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors ${theme.text} text-sm`}
              >
                <Upload size={16} />
                <span>Bulk Upload</span>
              </button>
              <button
                onClick={handleOpenAdd}
                className={`flex items-center gap-x-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm`}
              >
                <UserPlus size={16} />
                <span>Add Member</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name..."
            className={`w-full pl-9 pr-9 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className={`absolute right-3 top-1/2 -translate-y-1/2 ${theme.textMuted} hover:text-gray-600`}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Skill Level Filter */}
        <div className="relative">
          <select
            value={skillFilter}
            onChange={(e) => setSkillFilter(e.target.value)}
            className={`appearance-none pl-3 pr-8 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer`}
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
          <ChevronDown size={14} className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${theme.textMuted}`} />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`appearance-none pl-3 pr-8 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer`}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="pending">Pending Approval</option>
          </select>
          <ChevronDown size={14} className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${theme.textMuted}`} />
        </div>

        {/* Class Filter */}
        {allClasses.length > 0 && (
          <div className="relative">
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className={`appearance-none pl-3 pr-8 py-2 border ${theme.border} rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer`}
            >
              <option value="all">All Classes</option>
              {allClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
            <ChevronDown size={14} className={`absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${theme.textMuted}`} />
          </div>
        )}

        {/* Dues Pending Toggle */}
        <button
          onClick={() => setDuesPendingOnly(v => !v)}
          className={`flex items-center gap-x-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${
            duesPendingOnly
              ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600'
              : `${theme.border} ${theme.cardHover} ${theme.text}`
          }`}
        >
          <DollarSign size={14} />
          <span>Dues Pending</span>
        </button>

        {/* Clear all filters */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className={`flex items-center gap-x-1 px-3 py-2 border ${theme.border} rounded-lg ${theme.cardHover} transition-colors text-sm ${theme.text}`}
          >
            <X size={14} />
            <span>Clear All</span>
          </button>
        )}

        {hasActiveFilters && (
          <span className={`text-sm ${theme.textSecondary}`}>
            {filteredStudents.length} result{filteredStudents.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Bulk action bar */}
      {isAdmin && selectedMemberIds.size > 0 && (
        <div className={`flex flex-wrap items-center justify-between gap-3 mb-6 px-4 py-3 border ${theme.border} rounded-lg bg-blue-50 dark:bg-blue-900/20`}>
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-blue-600" />
            <span className={`text-sm font-medium ${theme.text}`}>
              {selectedMemberIds.size} student{selectedMemberIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleBulkResendInvite}
              disabled={bulkActionBusy}
              className={`flex items-center gap-x-1.5 px-3 py-1.5 border ${theme.border} rounded-lg text-sm ${theme.text} ${theme.cardHover} transition disabled:opacity-50`}
            >
              <Send size={14} />
              <span>Resend Invite</span>
            </button>
            <button
              onClick={() => handleBulkSuspend(true)}
              disabled={bulkActionBusy}
              className="flex items-center gap-x-1.5 px-3 py-1.5 border border-yellow-400 text-yellow-700 rounded-lg text-sm hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition disabled:opacity-50"
            >
              <span>Suspend</span>
            </button>
            <button
              onClick={() => handleBulkSuspend(false)}
              disabled={bulkActionBusy}
              className="flex items-center gap-x-1.5 px-3 py-1.5 border border-green-400 text-green-700 rounded-lg text-sm hover:bg-green-50 dark:hover:bg-green-900/20 transition disabled:opacity-50"
            >
              <span>Reactivate</span>
            </button>
            <button
              onClick={() => setBulkRemoveConfirmOpen(true)}
              disabled={bulkActionBusy}
              className="flex items-center gap-x-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm transition disabled:opacity-50"
            >
              <Trash2 size={14} />
              <span>Remove</span>
            </button>
            <button
              onClick={clearSelection}
              className={`flex items-center gap-x-1 px-2 py-1.5 ${theme.textSecondary} hover:${theme.text} text-sm`}
            >
              <X size={14} />
              <span>Clear</span>
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className={`ml-3 text-sm ${theme.textSecondary}`}>Loading members…</span>
        </div>
      )}

      {/* Grid View */}
      {!isLoading && viewMode === 'grid' && (
        filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filteredStudents.map(student => {
              const totalSessAttended = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.sessionsAttended, 0);
              const totalSess = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.totalSessions, 0);
              return (
                <div key={student.userId} className={`${theme.card} p-5 rounded-xl text-center flex flex-col relative`}>
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={selectedMemberIds.has(student.memberId)}
                      onChange={() => toggleSelectMember(student.memberId)}
                      aria-label={`Select ${student.firstName} ${student.lastName}`}
                      className="absolute top-4 left-4 w-4 h-4 rounded cursor-pointer"
                    />
                  )}
                  {/* Status badge */}
                  <div className="flex justify-end mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      student.isSuspended ? 'bg-gray-200 text-gray-700' :
                      student.status === 'active' ? 'bg-green-100 text-green-700' :
                      student.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {student.isSuspended ? 'Suspended' :
                       student.status === 'active' ? 'Active' :
                       student.status === 'pending' ? 'Pending' : 'Invited'}
                    </span>
                  </div>

                  <ProfileAvatar
                    userId={student.userId}
                    profileImage={student.profileImage}
                    firstName={student.firstName}
                    lastName={student.lastName}
                    size="2xl"
                    className="mx-auto mb-3 ring-2 ring-offset-2 ring-gray-100"
                  />
                  <h6 className={`font-semibold ${theme.text} mb-1`}>
                    {student.firstName} {student.lastName}
                  </h6>
                  <span className={`inline-block px-2.5 py-1 bg-${getLevelColor(student.skillLevel)}-100 text-${getLevelColor(student.skillLevel)}-700 rounded text-xs font-semibold mb-3 capitalize`}>
                    {student.skillLevel}
                  </span>

                  <div className="space-y-2 text-sm flex-1">
                    <div className={`flex justify-between items-center py-1 border-b ${theme.border}`}>
                      <span className={theme.textSecondary}>Classes</span>
                      <span className={`font-semibold ${theme.text}`}>{student.enrolledClasses}</span>
                    </div>
                    <div className={`flex justify-between items-center py-1 border-b ${theme.border}`}>
                      <span className={theme.textSecondary}>Sessions</span>
                      <span className={`font-semibold ${theme.text}`}>{totalSessAttended}/{totalSess}</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className={theme.textSecondary}>Attendance</span>
                      <div className="flex items-center">
                        <AttendanceTrendIcon rate={student.attendanceRate} />
                        <span className={`font-semibold ${theme.text}`}>{student.attendanceRate}%</span>
                      </div>
                    </div>
                    {student.dues && student.dues.amount > 0 && (
                      <div className={`flex justify-between items-center py-1 border-t ${theme.border}`}>
                        <span className={theme.textSecondary}>Dues</span>
                        <span className="font-semibold text-red-500">${student.dues.amount}</span>
                      </div>
                    )}
                  </div>

                  <div className={`flex gap-2 mt-4 flex-wrap`}>
                    {student.status === 'pending' && isAdmin ? (
                      <>
                        <button
                          onClick={() => {
                            setStudents(prev => prev.map(s => s.userId === student.userId ? { ...s, status: 'active' } : s));
                            toast.success(`${student.firstName} approved`);
                          }}
                          className="flex-1 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setStudents(prev => prev.filter(s => s.userId !== student.userId));
                            toast.success(`${student.firstName}'s request declined`);
                          }}
                          className="flex-1 py-1.5 border border-red-400 text-red-500 rounded-lg text-xs font-medium hover:bg-red-50 transition"
                        >
                          Decline
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setSelectedStudent(student)}
                          className={`flex-1 flex items-center justify-center gap-x-1 px-3 py-1.5 border ${theme.border} ${theme.text} rounded-lg ${theme.cardHover} transition text-xs font-medium`}
                        >
                          <Users size={13} />
                          <span>View</span>
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => handleOpenEdit(student)}
                              className="flex-1 flex items-center justify-center gap-x-1 px-3 py-1.5 border border-blue-400 text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition text-xs font-medium"
                            >
                              <Edit2 size={13} />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => setRemovingStudent(student)}
                              className="flex-1 flex items-center justify-center gap-x-1 px-3 py-1.5 border border-red-400 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition text-xs font-medium"
                            >
                              <Trash2 size={13} />
                              <span>Remove</span>
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`${theme.card} text-center p-14 rounded-xl`}>
            <Users size={48} className={`mx-auto mb-3 ${theme.textMuted}`} />
            <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No members found</h5>
            <p className={theme.textSecondary}>
              {hasActiveFilters ? 'Try adjusting your search or filters' : 'Add your first member to get started'}
            </p>
            {isAdmin && !hasActiveFilters && (
              <button
                onClick={handleOpenAdd}
                className={`mt-4 inline-flex items-center gap-x-2 px-5 py-2.5 ${theme.buttonPrimary} rounded-lg`}
              >
                <UserPlus size={18} />
                <span>Add Member</span>
              </button>
            )}
          </div>
        )
      )}

      {/* Table View */}
      {!isLoading && viewMode === 'table' && (
        filteredStudents.length > 0 ? (
          <div className={`${theme.card} border ${theme.border} rounded-xl overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${theme.background} border-b ${theme.border}`}>
                  <tr>
                    {isAdmin && (
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all visible students"
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                      </th>
                    )}
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Member</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Status</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Level</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Classes</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Attendance</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Dues</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Top Ranking</th>
                    <th className={`text-left px-4 py-3 font-medium ${theme.textSecondary}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(student => {
                    const totalSessAttended = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.sessionsAttended, 0);
                    const totalSess = (student.enrolledClassesList || []).reduce((sum, c) => sum + c.totalSessions, 0);
                    const attPct = totalSess > 0 ? Math.round((totalSessAttended / totalSess) * 100) : 0;
                    const topRanking = (student.sportRankings || []).sort((a, b) => a.rank - b.rank)[0];
                    return (
                      <tr key={student.userId} className={`border-b ${theme.border} last:border-0 ${theme.cardHover} transition-colors`}>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedMemberIds.has(student.memberId)}
                              onChange={() => toggleSelectMember(student.memberId)}
                              aria-label={`Select ${student.firstName} ${student.lastName}`}
                              className="w-4 h-4 rounded cursor-pointer"
                            />
                          </td>
                        )}
                        {/* Name + Avatar */}
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setSelectedStudent(student)}
                            className="flex items-center gap-x-3 hover:opacity-80 transition-opacity"
                          >
                            <ProfileAvatar
                              userId={student.userId}
                              profileImage={student.profileImage}
                              firstName={student.firstName}
                              lastName={student.lastName}
                              size="md"
                              className="flex-shrink-0"
                            />
                            <div className="text-left">
                              <p className={`font-semibold ${theme.text}`}>{student.firstName} {student.lastName}</p>
                              <p className={`text-xs ${theme.textSecondary}`}>{student.email}</p>
                            </div>
                          </button>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            student.isSuspended ? 'bg-gray-200 text-gray-700' :
                            student.status === 'active' ? 'bg-green-100 text-green-700' :
                            student.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {student.isSuspended ? 'Suspended' :
                             student.status === 'active' ? 'Active' :
                             student.status === 'pending' ? 'Pending' : 'Invited'}
                          </span>
                        </td>

                        {/* Level */}
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 bg-${getLevelColor(student.skillLevel)}-100 text-${getLevelColor(student.skillLevel)}-700 rounded text-xs font-semibold capitalize`}>
                            {student.skillLevel}
                          </span>
                        </td>

                        {/* Classes */}
                        <td className={`px-4 py-3 ${theme.text}`}>{student.enrolledClasses}</td>

                        {/* Attendance */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-x-1.5">
                            <AttendanceTrendIcon rate={attPct} />
                            <span className={`font-medium ${theme.text}`}>{attPct}%</span>
                            <span className={`text-xs ${theme.textSecondary}`}>({totalSessAttended}/{totalSess})</span>
                          </div>
                        </td>

                        {/* Dues */}
                        <td className="px-4 py-3">
                          {student.dues && student.dues.amount > 0
                            ? <span className="font-semibold text-red-500">${student.dues.amount}</span>
                            : <span className={theme.textSecondary}>—</span>
                          }
                        </td>

                        {/* Top Ranking */}
                        <td className="px-4 py-3">
                          {topRanking
                            ? (
                              <span className={`text-xs font-medium ${theme.text}`}>
                                #{topRanking.rank} {topRanking.sport}
                              </span>
                            )
                            : <span className={theme.textSecondary}>—</span>
                          }
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {student.status === 'pending' && isAdmin ? (
                              <>
                                <button
                                  onClick={() => {
                                    setStudents(prev => prev.map(s => s.userId === student.userId ? { ...s, status: 'active' } : s));
                                    toast.success(`${student.firstName} approved`);
                                  }}
                                  className="flex items-center gap-x-1 px-2.5 py-1 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition"
                                >
                                  <span>Approve</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setStudents(prev => prev.filter(s => s.userId !== student.userId));
                                    toast.success(`${student.firstName}'s request declined`);
                                  }}
                                  className="flex items-center gap-x-1 px-2.5 py-1 border border-red-400 text-red-500 rounded-lg text-xs font-medium hover:bg-red-50 transition"
                                >
                                  <span>Decline</span>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setSelectedStudent(student)}
                                  className={`flex items-center gap-x-1 px-2.5 py-1 border ${theme.border} rounded-lg ${theme.cardHover} text-xs font-medium ${theme.text} transition`}
                                >
                                  <Users size={12} />
                                  <span>View</span>
                                </button>
                                {isAdmin && (
                                  <>
                                    <button
                                      onClick={() => handleOpenEdit(student)}
                                      className="flex items-center gap-x-1 px-2.5 py-1 border border-blue-400 text-blue-500 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-medium transition"
                                    >
                                      <Edit2 size={12} />
                                      <span>Edit</span>
                                    </button>
                                    <button
                                      onClick={() => setRemovingStudent(student)}
                                      className="flex items-center gap-x-1 px-2.5 py-1 border border-red-400 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium transition"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={`${theme.card} text-center p-14 rounded-xl`}>
            <Users size={48} className={`mx-auto mb-3 ${theme.textMuted}`} />
            <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No members found</h5>
            <p className={theme.textSecondary}>
              {hasActiveFilters ? 'Try adjusting your search or filters' : 'Add your first member to get started'}
            </p>
            {isAdmin && !hasActiveFilters && (
              <button
                onClick={handleOpenAdd}
                className={`mt-4 inline-flex items-center gap-x-2 px-5 py-2.5 ${theme.buttonPrimary} rounded-lg`}
              >
                <UserPlus size={18} />
                <span>Add Member</span>
              </button>
            )}
          </div>
        )
      )}

      {/* Modals */}
      <StudentFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingStudent(null); }}
        onSave={handleSaveStudent}
        editingStudent={editingStudent}
      />
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Add or Invite Members"
        checkIfAlreadyExists={checkIfAlreadyExistsMember}
        onAddExistingMember={handleAddExistingMember}
        onInviteByEmail={handleInviteByEmail}
        onInviteByPhone={handleInviteByPhone}
      />
      <BulkUploadModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onUploaded={handleBulkUploaded}
        academyId={academyId}
      />
      <RemoveConfirm
        student={removingStudent}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemovingStudent(null)}
      />
      <BulkRemoveConfirm
        isOpen={bulkRemoveConfirmOpen}
        count={selectedMemberIds.size}
        onConfirm={handleBulkRemove}
        onCancel={() => setBulkRemoveConfirmOpen(false)}
      />
    </div>
  );
};

export default StudentsTab;
